import * as React from "react";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useInvoiceRepository } from "../../../api/repositories/InvoiceRepository";
import { AgGridReact } from "ag-grid-react";
import { ColDef, GetRowIdParams, GridApi, GridOptions, IServerSideGetRowsParams } from "ag-grid-enterprise";
import { formatDateWithoutTime } from "../../../utils/FormatUtils";
import { FilterDef } from "../../../constants/FinanceModels";
import FinanceDialog from "../../../component/FinanceDialog";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import InvoiceTableActions, { InvoicePageType } from "../components/InvoiceTableActions";
import { ID_CREDIT_NOTE } from "../constants/InvoicesTypes";
import { showStatusColumn } from "./InvoiceTableUtil";
import AgGridCustomTable from "../../../../../util/component/AgGridCustomTable";
import EmptyState from "../../../../../util/component/EmptyState";

const InvoiceLinkedTable = (props: { invoice: any, invoiceParent?: any }) => {

    const { t } = useTranslation("finance", { keyPrefix: "invoices" });
    const { getInvoicesByParentId, deleteInvoice } = useInvoiceRepository();

    const gridRef = useRef<AgGridReact>(null);

    const [warningOpen, setWarningOpen] = useState(false);
    const [warningTitle, setWarningTitle] = useState("");

    const columnDefs : ColDef[] = [
        {
            field: "invoiceNumber", headerName: t("tables.invoice.id-proforma"),
            minWidth: 150,
            unSortIcon: true, sortable: true,
            pinned: "left", lockPosition: "left", lockVisible: true, lockPinned: true,
        },
        {
            field: "sapInvoiceNumber", headerName: t("tables.invoice.id-invoice"),
            minWidth: 150,
            unSortIcon: false, sortable: true,
            pinned: "left", lockPosition: "left", lockVisible: true, lockPinned: true,
        },
        {
            field: "invoiceDate", headerName: t("tables.invoice.invoice-date"),
            minWidth: 200,
            unSortIcon: true, sortable: true,
            cellRenderer: (params: any) => formatDateWithoutTime(params.value),
        },
        {
            field: "documentType", headerName: t("tables.variation-notes.type"),
            minWidth: 180,
            unSortIcon: true, sortable: true,
            valueFormatter: params => t(`types.${params.value}`)
        },
        {
            field: "status", headerName: t("tables.variation-notes.status"),
            minWidth: 150,
            unSortIcon: true, sortable: true,
            pinned: "right", lockPosition: "right", lockVisible: true, lockPinned: true,
            cellRendererSelector: (params) => {
                let status: string = params.data.status;
                return showStatusColumn(status);
            },
        },
        {
            field: "_actions", headerName: '',
            minWidth: 100, maxWidth: 110,
            unSortIcon: false, sortable: false,
            pinned: "right", lockPosition: "right", lockVisible: true, lockPinned: true,
            cellRendererSelector: (params) => {
                return {
                    component: () => (<InvoiceTableActions page={InvoicePageType.LINKED} id={params.data.id} invoiceId={params.data.invoiceNumber}
                                                           invoiceStatus={params.data.status}
                                                           setWarningOpen={setWarningOpen}
                                                           setWarningTitle={setWarningTitle}/>),
                };
            },
            suppressFiltersToolPanel: true,
        },
    ];

    const gridOptions: GridOptions = {
        getRowId: (params: GetRowIdParams) => params.data['id'],
        defaultColDef: {
            sortable: true,
            resizable: true,
            wrapHeaderText: false,
        },
        noRowsOverlayComponentParams: { title: t('tables.variation-notes.no-rows-overlay.title'), content: t('tables.variation-notes.no-rows-overlay.content') }
    };


    const createDatasource = (filterDefs: FilterDef[], api: GridApi) => {
        let id = props.invoice.documentType === ID_CREDIT_NOTE ? props.invoice.parentId : props.invoice.id;

        return {
            getRows: async (params: IServerSideGetRowsParams) => {
                let startRow = params.request.startRow ? params.request.startRow : 0;
                let endRow = params.request.endRow ? params.request.endRow : 10;
                let sort = params.request.sortModel.length === 0 ? null : params.request.sortModel[0].colId;
                let order = params.request.sortModel.length === 0 ? null : params.request.sortModel[0].sort;

                getInvoicesByParentId(id, undefined, undefined, startRow, endRow - startRow, sort, order, props.invoice.legalEntity, props.invoice.dealer)
                        .then((response) => {
                            let result = response.result;
                            if (props.invoiceParent && startRow === 0) {
                                result.content = result.content.filter((it: any) => it.id !== props.invoice.id);
                                result.content.unshift(props.invoiceParent);

                                if (result.page.totalElements === 0) {
                                    result.page.totalElements += 1;
                                }
                            }
                            return result;
                        })
                        .then((result: any) => {
                            params.success({
                                rowData: result.content,
                                rowCount: result.page.totalElements,
                            });
                        })
                        .catch(() => params.fail())
                        .finally(() => {
                            if (api.getModel().getRowCount() === 0) {
                                api.showNoRowsOverlay();
                            } else {
                                api.hideOverlay();
                            }
                        });
            }
        };
    }


    const noRowsOverlayComponent = useMemo<any>(() => {
        return EmptyState;
    }, []);

    return (
            <div style={{ marginTop: '15px' }}>
                <AgGridCustomTable
                        rowModelType='serverSide'
                        paginationPageSize={10} serverSideInfiniteScroll
                        gridRef={gridRef} columnDefs={columnDefs} gridOptions={gridOptions}
                        noRowsOverlayComponent={noRowsOverlayComponent}
                        suppressRowClickSelection
                        suppressCellFocus
                        onGridReady={() => {
                            gridRef.current!.api.setServerSideDatasource(createDatasource([], gridRef.current!.api));
                        }}
                >
                </AgGridCustomTable>
                <FinanceDialog open={warningOpen}
                               onClose={() => setWarningOpen(false)}
                               callback={() => deleteInvoice(warningTitle).then(() => gridRef.current?.api?.setServerSideDatasource(createDatasource([], gridRef.current.api)))}
                               titleIcon={<InfoRoundedIcon htmlColor={"#C8017D"}/>}
                               title={t('delete-confirmation.title')}
                               content={t('delete-confirmation.text', { invoiceId: warningTitle })}
                               confirmButton={t('delete-confirmation.confirm')}
                               cancelButton={t('delete-confirmation.cancel')}
                />
            </div>
    )
}

export default InvoiceLinkedTable;